import { useState } from "react";
import { Modal } from "react-bootstrap";
import ImageCarousel from "./ImageCarousel";

function MapPlantationImagesOnPinpoint({ images }) {
  const [show, setShow] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const maxVisible = 10;
  const visibleImages = images.slice(0, maxVisible);
  const extraCount = images.length - maxVisible;

  const handleOpen = (idx) => {
    setStartIndex(idx);
    setShow(true);
  };

  const handleClose = () => setShow(false);

  // Layout params
  const imageSize = 100; // px
  const smallGap = 10; // px gap for 2-4 images
  const stackOffset = 20; // px offset for stacking 5+ images

  // Calculate container width and positioning based on image count
  let containerWidth = imageSize;
  let positions = [];

  if (visibleImages.length === 1) {
    // Single image centered, no offset
    containerWidth = imageSize;
    positions = [0];
  } else if (visibleImages.length <= 4) {
    // Side by side with small gap
    containerWidth = imageSize + (visibleImages.length - 1) * (imageSize - smallGap);
    positions = visibleImages.map((_, idx) => idx * (imageSize - smallGap));
  } else {
    // Stack with offset
    containerWidth = imageSize + (visibleImages.length - 1) * stackOffset;
    positions = visibleImages.map((_, idx) => idx * stackOffset);
  }

  return (
    <>
      <div
        className="image-stack-wrapper"
        role="button"
        tabIndex={0}
        onClick={() => handleOpen(0)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleOpen(0);
        }}
        aria-label="Click to view all images"
      >
        <div
          className="image-stack-container"
          style={{ width: `${containerWidth}px`, height: `${imageSize}px` }}
        >
          {visibleImages.map((img, idx) => {
            const left = positions[idx];
            // zIndex: top image highest
            const zIndex = visibleImages.length - idx;

            return (
              <div
                key={idx}
                className="image-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen(idx);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    handleOpen(idx);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View image ${idx + 1}`}
                style={{
                  left: `${left}px`,
                  zIndex,
                  width: `${imageSize}px`,
                  height: `${imageSize}px`,
                }}
              >
                <img
                  src={img}
                  alt={`Plantation ${idx + 1}`}
                  className="stacked-image"
                  draggable={false}
                />
                {idx === maxVisible - 1 && extraCount > 0 && (
                  <div className="extra-count-overlay">+{extraCount}</div>
                )}
              </div>
            );
          })}

          <div className="hover-overlay" style={{ width: `${containerWidth}px`, height: `${imageSize}px` }}>
            <span>Click to view all images</span>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal */}
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Body>
          <ImageCarousel images={images} startIndex={startIndex} />
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .image-stack-wrapper {
          display: flex;
          justify-content: center;
          cursor: pointer;
          user-select: none;
          outline: none;
          position: relative;
          margin: 0 auto;
          width: fit-content;
        }

        .image-stack-container {
          position: relative;
          height: 100px;
        }

        .image-wrapper {
          position: absolute;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          background: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .image-wrapper:hover,
        .image-wrapper:focus {
          transform: scale(1.1);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
          z-index: 999 !important;
        }

        .stacked-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          pointer-events: none;
          user-select: none;
        }

        .extra-count-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          font-weight: 700;
          font-size: 1.5rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
          pointer-events: none;
          user-select: none;
        }

        .hover-overlay {
          position: absolute;
          top: 0;
          left: 0;
          background: rgba(0, 0, 0, 0);
          color: white;
          font-weight: 600;
          font-size: 1rem;
          border-radius: 8px;
          display: flex;
          justify-content: center;
          align-items: center;
          pointer-events: none;
          user-select: none;
          transition: background 0.3s ease;
          z-index: 1000;
        }

        .image-stack-wrapper:hover .hover-overlay {
          background: rgba(0, 0, 0, 0.5);
          pointer-events: auto;
        }
      `}</style>
    </>
  );
}

export default MapPlantationImagesOnPinpoint;